import React from 'react'
import { WidgetProps } from '../types'
import { BaseWidget } from '../BaseWidget'
import { getPieColors, PieGraph } from '../Atoms/PieGraph'
import { Flex } from 'components'
import { WidgetTitle } from '../Atoms'
import {
  BaseColorType,
  borderRadius,
  color,
  Divider,
  Dot,
  Popover,
  shadow,
  Typography,
} from '@evrekadev/evreka-ui-components'
import styled, { css } from 'styled-components'
import { SquareSkeleton } from 'components/Skeletons/SquareSkeleton'
import { TypographySkeleton } from 'components/Skeletons/TypographySkeleton'

type PartToWholeData = Array<{
  key: string
  value: number
  color?: BaseColorType
  label?: string
}>

export type PartToWholeWidgetProps = WidgetProps<PartToWholeData>

export const PartToWholeWidget: React.FC<PartToWholeWidgetProps> = (props) => {
  return (
    <BaseWidget size={props.size}>
      <Content {...props} />
    </BaseWidget>
  )
}

const Content: React.FC<PartToWholeWidgetProps> = (props) => {
  if (props.size === '2x2') {
    return <LGContent {...props} />
  } else if (props.size === '1x2') {
    return <MDContent {...props} />
  }
  return <SMContent {...props} />
}

const SMContent: React.FC<PartToWholeWidgetProps> = (props) => {
  return (
    <Flex
      directionColumn="zero"
      between="zero"
      fullHeight
      style={{
        overflow: 'visible',
      }}
    >
      <WidgetTitle>{props.title}</WidgetTitle>
      <Flex middle="zero" gap={1}>
        <PieGraph loading={props.loading} data={props.data} size={140} />
        <Legend {...props} />
      </Flex>
    </Flex>
  )
}

const MDContent: React.FC<PartToWholeWidgetProps> = (props) => {
  return (
    <Flex
      directionColumn="zero"
      between="zero"
      fullHeight
      fullWidth
      style={{
        overflow: 'visible',
      }}
    >
      <WidgetTitle>{props.title}</WidgetTitle>
      <Flex fullWidth gap={2} bottom="zero">
        <PieGraph loading={props.loading} data={props.data} />
        <Legend {...props} />
      </Flex>
    </Flex>
  )
}

const LGContent: React.FC<PartToWholeWidgetProps> = (props) => {
  return (
    <Flex
      directionColumn="zero"
      between="zero"
      fullHeight
      style={{
        overflow: 'visible',
      }}
    >
      <WidgetTitle>{props.title}</WidgetTitle>
      <Flex fullHeight directionColumn="zero" middle="zero" center="zero" gap={2}>
        <PieGraph loading={props.loading} data={props.data} size={180} />
        <Legend {...props} />
      </Flex>
    </Flex>
  )
}

const Legend: React.FC<PartToWholeWidgetProps> = ({ data, size, loading }) => {
  if (loading) {
    return (
      <Flex
        gap={0.5}
        directionColumn="zero"
        middle="zero"
        fullWidth
        style={{
          flexGrow: 1,
          overflow: 'hidden',
        }}
      >
        {new Array(4).fill(0).map((_, i) => (
          <Flex gap={0.5} middle="zero" key={i}>
            <SquareSkeleton
              style={{
                flexShrink: 0,
              }}
              height={'1.25rem'}
              width={'0.5rem'}
            />
            <TypographySkeleton fontSize="text_sm" />
          </Flex>
        ))}
      </Flex>
    )
  }
  if (!data || data.length === 0) {
    return (
      <Flex fullHeight fullWidth center="zero" middle="zero">
        <Typography fontSize="text_sm" color="grey700">
          No Data.
        </Typography>
      </Flex>
    )
  }
  const hasMoreThanFourItems = data.length > 4
  const sliceEnd = hasMoreThanFourItems ? 3 : data.length
  return (
    <Flex
      gap={0.5}
      directionColumn="zero"
      middle="zero"
      fullWidth
      fullHeight
      style={{
        flexGrow: 1,
        overflow: 'hidden',
      }}
    >
      {data.slice(0, sliceEnd).map((item, index) => {
        const color = getPieColors(data)[index]
        return (
          <React.Fragment key={index}>
            <Flex gap={0.5} middle="zero" fullWidth key={index}>
              <StyledDot color={color} />
              <Typography
                fontSize="text_sm"
                color="grey700"
                style={{
                  flexGrow: 1,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
                noWrap
              >
                {item.label ?? item.key}
              </Typography>
              {size !== '1x1' && (
                <Typography fontSize="text_sm" color="grey600" className="jetbrains-mono">
                  {item.value.toLocaleString()}
                </Typography>
              )}
            </Flex>
            {size !== '1x1' && index !== data.length - 1 && <Divider />}
          </React.Fragment>
        )
      })}
      {hasMoreThanFourItems && (
        <div style={{ width: '100%' }}>
          <Popover
            componentSize="large"
            customButton={
              <Flex
                gap={0.5}
                middle="zero"
                style={{
                  cursor: 'pointer',
                }}
                fullWidth
              >
                <StyledDot ghost />
                <Typography fontSize="text_sm" color="grey700" fontWeight="semibold">
                  +{data.length - sliceEnd} {size !== '1x1' && 'items'}
                </Typography>
              </Flex>
            }
            customContent={
              <Flex
                gap={0.5}
                directionColumn="zero"
                style={{
                  padding: size === '1x1' ? '0' : '0.5rem',
                }}
              >
                {data.slice(sliceEnd).map((item, index) => {
                  const color = getPieColors(data)[index]
                  return (
                    <React.Fragment key={index}>
                      <Flex gap={0.5} middle="zero" key={index}>
                        <StyledDot color={color} />
                        <Typography
                          fontSize="text_sm"
                          color="grey700"
                          style={{
                            flexGrow: 1,
                          }}
                        >
                          {item.label ?? item.key}
                        </Typography>
                        <Typography fontSize="text_sm" color="grey600" className="jetbrains-mono">
                          {item.value}
                        </Typography>
                      </Flex>
                      {size !== '1x1' && index !== data.length - 4 && <Divider />}
                    </React.Fragment>
                  )
                })}
              </Flex>
            }
            portal
          />
        </div>
      )}
    </Flex>
  )
}

const StyledDot = styled(Dot)<{
  ghost?: boolean
}>`
  ${shadow('xs')}
  ${borderRadius(2)}
  height: 1.25rem;
  width: 0.5rem;
  flex-shrink: 0;
  ${({ ghost }) =>
    ghost &&
    css`
      background-color: transparent;
      border: 1px solid ${color('grey400')};
    `}
`
